import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatError } from "@/lib/api";
import { Check, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { toast } from "sonner";

export default function CoursePlayer() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [current, setCurrent] = useState(null);

  const load = () => api.get(`/my/course/${slug}`).then(({ data }) => {
    setCourse(data);
    // pick first not-completed lesson
    const all = data.modules.flatMap((m) => m.lessons);
    const first = all.find((l) => !data.progress[l.lesson_id]) || all[0];
    setCurrent((c) => c || first);
  }).catch((e) => toast.error(formatError(e)));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (!course || !current) return <PublicLayout><div className="container-x py-24">Loading...</div></PublicLayout>;
  const allLessons = course.modules.flatMap((m) => m.lessons);
  const idx = allLessons.findIndex((l) => l.lesson_id === current.lesson_id);
  const prev = allLessons[idx - 1];
  const next = allLessons[idx + 1];

  const markComplete = async () => {
    await api.post(`/my/course/${course.course_id}/progress`, { lesson_id: current.lesson_id, completed: true });
    toast.success("Marked complete");
    await load();
    if (next) setCurrent(next);
  };

  const embed = (url) => {
    if (!url) return "";
    if (url.includes("youtube.com/watch")) return url.replace("watch?v=", "embed/");
    if (url.includes("youtu.be/")) return url.replace("youtu.be/", "www.youtube.com/embed/");
    return url;
  };

  return (
    <PublicLayout>
      <section className="container-x pt-8 pb-20 grid lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3 space-y-6" data-testid="course-player">
          <Link to="/dashboard" className="text-xs text-ink-500 hover:text-ink-900">← Back to dashboard</Link>
          <div className="aspect-video rounded-2xl overflow-hidden glass">
            <iframe src={embed(current.video_url)} title={current.title} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
          </div>
          <div>
            <div className="overline mb-2">Lesson {idx + 1} / {allLessons.length}</div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{current.title}</h1>
            <p className="mt-3 text-ink-600 leading-relaxed">{current.description}</p>
          </div>
          <div className="flex items-center gap-3">
            <button disabled={!prev} onClick={() => prev && setCurrent(prev)} className="btn-ghost disabled:opacity-40" data-testid="player-prev"><ChevronLeft className="w-4 h-4" /> Previous</button>
            <button onClick={markComplete} className="btn-neon" data-testid="player-complete"><Check className="w-4 h-4 mr-1" /> Mark complete</button>
            <button disabled={!next} onClick={() => next && setCurrent(next)} className="btn-ghost disabled:opacity-40" data-testid="player-next">Next <ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <aside className="glass rounded-2xl p-4 max-h-[70vh] overflow-auto" data-testid="player-sidebar">
          <div className="overline mb-3 px-2">Curriculum</div>
          {course.modules.map((m) => (
            <div key={m.module_id} className="mb-4">
              <div className="text-xs uppercase tracking-wider text-ink-500 px-2 mb-2">{m.title}</div>
              {m.lessons.map((l) => {
                const done = course.progress[l.lesson_id];
                const active = l.lesson_id === current.lesson_id;
                return (
                  <button key={l.lesson_id} onClick={() => setCurrent(l)} className={`w-full text-left flex items-center gap-2 px-2 py-2 rounded-lg text-sm ${active ? "bg-gold/10 text-ink-900" : "text-ink-700 hover:bg-ink-900/[0.04]"}`} data-testid={`player-lesson-${l.lesson_id}`}>
                    {done ? <Check className="w-4 h-4 text-gold" /> : <Play className="w-3.5 h-3.5 text-ink-500" />}
                    <span className="flex-1">{l.title}</span>
                    <span className="text-xs text-ink-500">{l.duration}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </aside>
      </section>
    </PublicLayout>
  );
}
